import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { GridButton, GridRow, GridRowButton, GridRowGroupButton } from '../../grid.types';
import { GridService } from '../../grid.service';
import { TranslateService } from '@app/core/services/translate.service';
import { GridComponent } from '../../grid.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { GridModalService } from '../../modals/grid-modal.service';
import { PopupService } from '@app/core/services/popup.service';
import { ColDef, IRowNode } from '@ag-grid-community/core';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { GotoParms, StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { Base64UrlService } from '@app/services/base64url.service';
import { DatataskEvent, ModalEvent, NavigateEvent } from '../../cell-renderers/grid-functions-cell-renderer/grid-functions-cell-renderer.component';
import { isErrorOrValidation, isErrorResponse } from '../../helpers/grid.helpers';

@Component({
    selector: 'tt-grid-button',
    templateUrl: './grid-button.component.html',
    styleUrls: ['./grid-button.component.css'],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class GridButtonComponent<TType extends GridRow = GridRow> extends ComponentBaseComponent implements OnChanges {
    @Input()
    public ttContext: 'toolbar' | 'row' | 'rowGroup' = 'toolbar';

    /**
     * The text to display in the button.
     */
    @Input()
    public ttId: string = '';

    /**
     * The text to display in the button.
     */
    @Input()
    public ttText: string = '';

    /**
     * Whether to translate the text to display in the button, default is `'true'`.
     */
    @Input()
    public get ttTranslateText(): boolean {
        return this._translateText;
    }
    public set ttTranslateText(value: BooleanInput | '1' | 1 | 0 | '0') {
        this._translateText = this.customCoerceBoolenProperty(value);
    }
    private _translateText = true;

    /**
     * Text to display in tooltip when hovering the button, can be used as helper text or to make an icon button accessible.
     */
    @Input()
    public ttTooltipText: string = '';

    /**
     * Whether the tooltip text should be translated or not, default is `true`.
     */
    @Input()
    public get ttTranslateTooltipText(): boolean {
        return this._translateTooltipText;
    }
    public set ttTranslateTooltipText(value: BooleanInput | '1' | 1 | 0 | '0') {
        this._translateTooltipText = this.customCoerceBoolenProperty(value);
    }
    private _translateTooltipText = true;

    @Input()
    ttIcon: string = '';

    @Input()
    ttSecondIcon: string = '';

    /**
     * The type of functionality provided in the button if click is not handled.
     */
    @Input()
    public ttType?: 'staticgoto' | 'statigoto' | 'goto' | 'datatask' | 'modal' | 'cell' | 'noclick' | 'print' | 'popup' | 'overflow' | undefined;

    /**
     * The variant of the button, default is `'primary'`.
     */
    @Input()
    public ttKind?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'link' | 'ghost-danger' | 'icon' | string;

    @Input()
    public ttModalWidth?: string;

    @Input()
    public ttModalHeight?: string;

    /**
     * Whether the button is disabled.
     */
    @Input()
    public get ttDisabled(): boolean | 'hidden' {
        return this._disabled;
    }
    public set ttDisabled(value: BooleanInput | '1' | 1 | 0 | '0' | 'hidden') {
        if (value == 'hidden') {
            this._disabled = 'hidden';
        } else {
            this._disabled = this.customCoerceBoolenProperty(value);
        }
    }
    private _disabled: boolean | 'hidden' = false;

    /**
     * If using default functionality whether the function should handle each call row by row (instead of simultanously), for selected rows.
     */
    @Input()
    public get ttRowByRow(): boolean {
        return this._rowByRow;
    }
    public set ttRowByRow(value: BooleanInput | '1' | 1 | 0 | '0') {
        this._rowByRow = this.customCoerceBoolenProperty(value);
    }
    private _rowByRow = false;

    /**
     * Whether to read the grid anew when the function has completed.
     *
     * @default false
     */
    @Input()
    public get ttReadOnClose(): boolean | undefined {
        return this._readOnClose;
    }
    public set ttReadOnClose(value: BooleanInput | '1' | 1 | 0 | '0' | undefined | null) {
        this._readOnClose = value === undefined || value === null ? undefined : this.customCoerceBoolenProperty(value);
    }
    private _readOnClose: boolean | undefined = false;

    /**
     * Whether to read the grid anew when the function has completed.
     *
     * @default false
     */
    @Input()
    public get ttRebindOnClose(): boolean | undefined {
        return this._rebindOnClose;
    }
    public set ttRebindOnClose(value: BooleanInput | '1' | 1 | 0 | '0' | undefined | null) {
        this._rebindOnClose = value === undefined || value === null ? undefined : this.customCoerceBoolenProperty(value);
    }
    private _rebindOnClose: boolean | undefined = false;

    /**
     * The name of the route to navigate. \
     * **NB.** only the name, exclude any parameters.
     */
    @Input()
    public ttGoto?: string = '';

    @Input()
    public ttGotoParms?: GotoParms<string>;

    @Input()
    public get ttP2DatataskKeyno(): number {
        return this._p2DatataskKeyno;
    }
    public set ttP2DatataskKeyno(value: NumberInput) {
        this._p2DatataskKeyno = coerceNumberProperty(value);
    }
    private _p2DatataskKeyno: number = 0;

    @Input()
    public ttModalComponent?: string | null = '';

    @Input()
    public ttModalSize?: 'pst-ninetyfive' | 'sm' | 'md' | 'lg';

    /**
     * Whether to override disabled prop and disable the button if no rows are selected in the grid.
     *
     * @default false
     */
    @Input()
    public get ttDisableIfNoSelected(): boolean {
        return this._disableIfNoSelected;
    }
    public set ttDisableIfNoSelected(value: BooleanInput | '1' | 1 | 0 | '0') {
        this._disableIfNoSelected = this.customCoerceBoolenProperty(value);
    }
    private _disableIfNoSelected = false;

    /**
     * Whether to save the selected rows with 616 before perfoming function.
     *
     * @default false
     */
    @Input()
    public get ttSaveSelectedRows(): boolean {
        return this._saveSelectedRows;
    }
    public set ttSaveSelectedRows(value: BooleanInput | '1' | 1 | 0 | '0') {
        this._saveSelectedRows = this.customCoerceBoolenProperty(value);
    }
    private _saveSelectedRows = false;

    /**
     * The grid this button is created for.
     */
    @Input()
    public grid?: GridComponent<TType>;

    /**
     * If grid button is used for a node, the node the button is presented for.
     */
    @Input()
    public node?: IRowNode;

    @Input()
    public colDef?: ColDef;

    @Input()
    public get ttForceNewTab(): boolean {
        return this._forceNewTab;
    }
    public set ttForceNewTab(value: BooleanInput | '1' | 1 | 0 | '0') {
        this._forceNewTab = this.customCoerceBoolenProperty(value);
    }
    private _forceNewTab = false;

    @Output()
    public ttClick = new EventEmitter<MouseEvent>();

    @Input()
    public ttItems: GridButton[] | undefined;

    @Input()
    public ttOverflowItemTextFallback?: (item: GridButton) => string;

    public text = '';

    public tooltipText = '';

    public loading = false;

    public icon = '';

    public secondIcon = '';

    public buttonClasses = 'ag-button ag-floating-filter-button-button tt-grid__toolbar-button';

    public get buttonType(): GridButton['type'] | 'statigoto' | undefined {
        return this.ttType;
    }

    constructor(
        public gridService: GridService,
        private base64: Base64UrlService,
        private translate: TranslateService,
        private datatask: DataTaskService,
        private modalService: ModalService,
        private popup: PopupService,
        private utility: UtilityService,
        private state: StateService,
        private gridModalService: GridModalService,
    ) {
        super();
    }

    public async onClick(event: MouseEvent, button?: GridButton) {
        this.ttClick.emit(event);

        if (!this.isValidButton() || event.defaultPrevented) return;

        try {
            this.loading = true;

            await this.gridButtonClick(event, button);
        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
        }
    }

    private async gridButtonClick(event: MouseEvent, button?: GridButton) {
        const gridButton = button;

        if (this.ttContext === 'row' || this.ttContext === 'rowGroup') {
            await this.rowButtonClick(event, gridButton);
        } else {
            await this.dbToolbarButtonClick(event, gridButton);
        }
    }

    private async dbToolbarButtonClick(event: MouseEvent, button?: GridButton) {
        if (!this.isValidButton()) return;

        try {
            if (this.ttSaveSelectedRows) {
                await this.saveSelectedRows();
            }

            const type = button?.type ?? this.ttType;

            if (type === 'datatask' && this.hasValidDatataskKeyno()) {
                await this.runDatataskByGridButton(event, button);
            } else if (type === 'modal') {
                await this.openModalByGridButton(button);
            } else if (type === 'print') {
                this.grid!.openPrintDialog(button);
            } else if (type === 'goto') {
                await this.gotoByGridButton(event, button);
            } else if (type === 'popup') {
                await this.openPopupByGridButton(button);
            }
        } catch (error) {
            console.error(error);
            this.modalService.openErrorDialog(`${error}`);
        }
    }

    private async rowButtonClick(event: MouseEvent, button?: GridButton) {
        try {
            if (this.ttContext !== 'rowGroup' && this.shouldSaveSelectedRows(button)) {
                await this.saveSelectedRows();
            }

            if (button && 'func' in button && button.func instanceof Function) {
                if (this.ttContext === 'rowGroup') {
                    await (button as GridRowGroupButton).func(this.node!, button as GridRowGroupButton, event);
                } else {
                    await (button as GridRowButton<TType>).func(this.node?.data, button as GridRowButton<TType>, event);
                }

                return;
            }

            const type = button?.type ?? this.ttType;

            if (!type) return;

            if (this.isStaticGotoType(type)) {
                this.staticGotoByGridButton(event, button);
            } else if (type === 'modal') {
                await this.openRowModalByGridButton(button);
            } else if (type === 'goto') {
                await this.gotoByRowButton(event, button);
            } else if (type === 'datatask') {
                await this.runDatataskByRowButton(event, button);
            } else if (type === 'popup') {
                await this.openRowPopupByGridButton(button);
            }
        } catch (error) {
            console.error(error);
            this.modalService.openErrorDialog(`${error}`);
        }
    }

    private async saveSelectedRows() {
        if (!this.grid!.options.dataTask?.loadData?.method) {
            console.error('Remember id on grid needs to be set in order to save the selected rows');
        } else {
            let encodedSelectedRows = '';
            try {
                encodedSelectedRows = this.gridService.encodeIsSelectedRows<TType>(this.grid!.options);
            } catch (error) {
                console.error(error);
            }

            await this.datatask.Post(616, { variablename: 'dtk_' + this.grid!.options.dataTask.loadData.method + '.selected_rows', variablevalue: encodedSelectedRows });
        }
    }

    private shouldSaveSelectedRows(button?: GridButton): boolean {
        return !!(button?.save_selected_rows ?? this.ttSaveSelectedRows) && [1, '1', true, 'true'].includes((button?.save_selected_rows ?? this.ttSaveSelectedRows) as any);
    }

    private staticGotoByGridButton(event: MouseEvent, button?: GridButton) {
        const navigateEvent: NavigateEvent = {
            preventDefault: () => (navigateEvent.defaultPrevented = true),
            defaultPrevented: false,
            newTab: button?._forceNewTab === true || this.ttForceNewTab || this.utility.isModifierKeyPressed(event),
            rowData: this.node?.data,
            itemState: this.node?.data?.['item_state'],
            itemParms: this.node?.data?.['item_parms'],
        };

        this.grid?.ttNavigate.emit(navigateEvent);

        if (navigateEvent.defaultPrevented) return;

        if (navigateEvent.newTab) {
            this.state.newTab(navigateEvent.itemState || '', navigateEvent.itemParms);
        } else {
            this.state.go(navigateEvent.itemState || '', navigateEvent.itemParms);
        }
    }

    private async openRowModalByGridButton(button?: GridButton) {
        if (!this.grid) return;

        const component = button?.component ?? this.ttModalComponent;

        if (!component) return;

        if (component === 'StateModal' || component === 'StateDialog') {
            let state = button?.state ?? this.ttGoto;
            let params = (button?.state_params ?? this.ttGotoParms ?? undefined) as string | undefined;
            let width: string | undefined = button?.modal_width || this.ttModalWidth || undefined;
            let height: string | undefined = button?.modal_height || this.ttModalHeight || undefined;

            if (!state) {
                const response = await this.gridService.getGotoParms({ field: button?.name ?? this.ttId, loadDataTask: this.grid.options.dataTask!.loadData!, rememberId: this.grid.options.dataTask!.rememberId ?? '', row: this.node?.data });

                state = response.item_state;
                params = response.item_parms;
            }

            if (!!state) {
                await this.modalService.openStateDialog({ state, params, height, width });
            } else {
                console.warn('TOUCHTIME ERROR - there was no state to open');
            }
        } else {
            const modalEvent: ModalEvent = {
                preventDefault: () => (modalEvent.defaultPrevented = true),
                defaultPrevented: false,
                modalComponent: component || '',
                modalSize: button?.modal_size || this.ttModalSize || 'pst-ninetyfive',
                rowData: this.node?.data,
            };

            this.grid.ttOpenModal.emit(modalEvent);

            if (modalEvent.defaultPrevented) return;

            await this.gridModalService.openDialogFromDbButton(button ?? { name: this.ttId, component }, {
                loadData: this.grid.options.dataTask?.loadData,
                rememberId: this.grid.options.dataTask?.rememberId,
                selectedRows: this.grid.options.gridfunc?.getIsSelectedRows() || [],
                row: this.node?.data,
            });
        }

        const rebindOnClose = button?.rebind_on_close ?? this.ttRebindOnClose;
        const readOnClose = button?.read_on_close ?? this.ttReadOnClose;

        if (rebindOnClose !== false) {
            this.grid.options.gridfunc?.rebind();
        } else if (readOnClose !== false) {
            this.grid.options.gridfunc?.read();
        }
    }

    private async gotoByRowButton(event: MouseEvent, button?: GridButton) {
        if (!this.grid) return;

        await this.gridService.dbButtonGoto({ button: button ?? ({ name: this.ttId, state: this.ttGoto, state_params: this.ttGotoParms } as any), event, navigateEventEmitter: this.grid.ttNavigate, row: this.node?.data, options: this.grid.options as any });
    }

    private async runDatataskByRowButton(event: MouseEvent, button?: GridButton) {
        const p2DatataskKeyno = button?.p2_datatask_keyno ?? this.ttP2DatataskKeyno;

        if (!p2DatataskKeyno || p2DatataskKeyno < 1 || !this.grid) return;

        const datataskEvent: DatataskEvent = {
            preventDefault: () => (datataskEvent.defaultPrevented = true),
            defaultPrevented: false,
            p2_datatask_keyno: p2DatataskKeyno,
            newTab: this.utility.isModifierKeyPressed(event),
            rowData: this.node?.data,
        };

        this.grid.ttDatatask.emit(datataskEvent);

        if (datataskEvent.defaultPrevented) return;

        await this.gridService.dbButtonDatatask({
            button: button ?? { p2_datatask_keyno: p2DatataskKeyno, state: this.ttGoto, state_params: this.ttGotoParms as string | undefined },
            parameters: {
                colgroup: this.node?.field,
                rowkey: this.node?.key,
                dataItem: this.node?.data,
                rows: this.grid.gridApi?.getSelectedRows(),
                isSelected: this.grid.options.data?.rowData.filter((data) => data['is_selected'] === true),
            },
            datataskEventEmitter: this.grid.ttDatatask,
            navigateEventEmitter: this.grid.ttNavigate,
            event,
            options: this.grid.options,
        });
    }

    private async openRowPopupByGridButton(button?: GridButton) {
        if (!this.grid) return;

        const buttonName = button?.name ?? this.ttId;
        const response = await this.gridService.getGotoParms({ loadDataTask: this.grid.options.dataTask?.loadData, field: `popup:${buttonName}`, row: this.node?.data, rememberId: this.grid.options.dataTask?.rememberId, colgroup: this.node?.field, rowkey: this.node?.key });

        if (!response.item_state) return;

        const popup = await this.popup.openPopup(response.item_state + '/' + response.item_parms, { rememberId: !!this.grid.options.dataTask?.rememberId ? `${this.grid.options.dataTask.rememberId}.popup:${buttonName}` : undefined });

        popup?.closed.subscribe({
            next: (closed) => {
                if (closed) {
                    this.gridService.onStateClosed({ colDef: this.colDef, node: this.node, grid: this.grid!, ...(button ?? {}) });

                    const rebindOnClose = button?.rebind_on_close ?? this.ttRebindOnClose;
                    const readOnClose = button?.read_on_close ?? this.ttReadOnClose;

                    if (rebindOnClose === true) {
                        this.grid?.options.gridfunc?.rebind();
                    } else if (readOnClose !== false) {
                        this.grid?.options.gridfunc?.read();
                    }
                }
            },
        });
    }

    private async runDatataskByGridButton(event: MouseEvent, button?: GridButton) {
        const rowByRow = button?.rowbyrow ?? this.ttRowByRow;

        if (rowByRow) {
            await this.runDatataskByGridButtonRowByRow(event, button);
        } else {
            await this.runDatatask({ event, button });
        }

        // this.gridService.onStateClosed({ grid: this.grid!, read_on_close: this.ttReadOnClose, rebind_on_close: this.ttRebindOnClose, node: this.node });
    }

    private async openModalByGridButton(button?: GridButton) {
        const component = button?.component ?? this.ttModalComponent;

        if (!component) return;

        let refreshAfterClose = true;

        if (component === 'StateDialog') {
            let { state, params } = this.getStateAndParams(button);
            let width: string | undefined = button?.modal_width || this.ttModalWidth || undefined;
            let height: string | undefined = button?.modal_height || this.ttModalHeight || undefined;

            if (!state) {
                const response = await this.gridService.getGotoParms({ loadDataTask: this.grid?.options.dataTask?.loadData, field: `popup:${this.ttId}`, row: this.grid?.gridApi?.getSelectedRows()[0], rememberId: this.grid?.options.dataTask?.rememberId, colgroup: '', rowkey: '' });

                if (!!response.item_state) {
                    state = response.item_state;
                    params = response.item_parms;
                }
            }

            if (!!state) {
                const response = await this.modalService.openStateDialog({ state, params, width, height });
                refreshAfterClose = true;
            }
        } else {
            const event: ModalEvent = {
                defaultPrevented: false,
                preventDefault: () => (event.defaultPrevented = true),
                modalComponent: component,
                rowData: null,
                modalSize: button?.modal_size ?? 'pst-ninetyfive',
            };

            this.grid!.ttOpenModal.emit(event);

            if (event.defaultPrevented) return;

            await this.gridModalService.openDialogFromDbButton(
                { component: component },
                {
                    selectedRows: this.grid!.options.gridfunc?.getIsSelectedRows() || [],
                    loadData: this.grid!.options.dataTask?.loadData,
                    rememberId: this.grid!.options.dataTask?.rememberId,
                },
            );
        }

        this.gridService.onStateClosed<TType>({ grid: this.grid!, read_on_close: (button?.read_on_close ?? this.ttReadOnClose) && refreshAfterClose, rebind_on_close: (button?.rebind_on_close ?? this.ttRebindOnClose) && refreshAfterClose, node: this.node });
    }

    private async gotoByGridButton(event: MouseEvent, button?: GridButton) {
        if (!this.grid) return;

        const selectedRow = this.grid.gridApi?.getSelectedRows()[0];

        let newTab = this.utility.isModifierKeyPressed(event);
        let itemState = button?.state ?? this.ttGoto;
        let itemParms = button?.state_params ?? this.ttGotoParms;

        try {
            itemParms = !!itemParms && typeof itemParms === 'string' ? this.gridService.atou(itemParms || '') : null;

            if (!!itemParms && itemParms.startsWith('{') && itemParms.endsWith('}')) {
                itemParms = JSON.parse(itemParms);
            }
        } catch (error) {
            itemParms = button?.state_params ?? this.ttGotoParms;
        }

        if (!itemState) {
            if (!!selectedRow && !!(button?.name ?? this.ttId)) {
                const parameters = await this.gridService.getParametersFromDataTaskId(this.grid.options, 'loadData');
                const response = await this.gridService.getGotoParms({ row: selectedRow, loadDataTask: { ...this.grid.options.dataTask?.loadData, parameters: parameters }, rememberId: this.grid.options.dataTask?.rememberId ?? '', field: button?.name ?? this.ttId });

                itemState = response.item_state;
                itemParms = response.item_parms;
            }
        }

        if (newTab) {
            this.state.newTab(itemState || '', itemParms);
        } else {
            this.state.go(itemState || '', itemParms);
        }
    }

    private async openPopupByGridButton(button?: GridButton) {
        let { state, params } = this.getStateAndParams(button);

        if (!state && !!this.grid!.gridApi && this.grid!.gridApi.getSelectedRows().length > 0) {
            const response = await this.gridService.getGotoParms({ loadDataTask: this.grid!.ttOptions.dataTask?.loadData, field: `popup:${button?.name ?? this.ttId}`, row: this.grid!.gridApi.getSelectedRows()[0], rememberId: this.grid!.ttOptions.dataTask?.rememberId, colgroup: '', rowkey: '' });

            if (!!response.item_state) {
                state = response.item_state;
                params = response.item_parms;
            }
        }

        if (!!state) {
            const popup = await this.popup.openPopup(state + (!!params ? '/' + params : ''), { rememberId: !!this.grid!.ttOptions.dataTask?.rememberId ? `${this.grid!.ttOptions.dataTask.rememberId}.toolbar:${this.ttId}` : undefined });

            popup?.closed.subscribe((val) => {
                if (val === true) {
                    this.gridService.onStateClosed<TType>({ grid: this.grid!, read_on_close: button?.read_on_close ?? this.ttReadOnClose, rebind_on_close: button?.rebind_on_close ?? this.ttRebindOnClose, node: this.node });
                }
            });
        }
    }

    private async runDatataskByGridButtonRowByRow(event: MouseEvent, button?: GridButton) {
        const rows = this.grid!.options.gridfunc?.getIsSelectedRows();

        if (!rows || rows.length === 0) return;
        let count = 0;
        let modalClosed = false;
        const progress = this.modalService.openProgressDialog({ value: count, max: rows.length, rememberId: !!this.grid!.options.dataTask?.rememberId ? this.grid!.options.dataTask.rememberId + '.progress' : undefined });

        progress.dialogRef.beforeClosed().subscribe({ next: () => (modalClosed = true) });

        for (let row of this.grid!.options.gridfunc?.getIsSelectedRows() ?? []) {
            try {
                if (modalClosed) break;
                let response = await this.runDatatask({ event, params: { ...row }, button, read: false });

                if (isErrorOrValidation(response) || modalClosed) {
                    if (isErrorResponse(response)) {
                        this.modalService.show({ title: 'error_occured', translateTitle: true, message: response[0]?.errormessage ?? '', type: 'warning' });
                    }

                    progress.updateStatus('invalid');
                    break;
                } else {
                    count++;
                    progress.updateValue(count);
                }

                if (count === rows.length) {
                    progress.updateStatus('finish');
                }
            } catch (error) {
                console.error(error);
                break;
            }
        }

        this.grid?.options?.gridfunc?.read();
    }

    private async runDatatask(params: { event: MouseEvent; params?: { [key: string]: any }; button?: GridButton; read?: boolean }) {
        try {
            return await this.gridService.dbButtonDatatask({
                button: { p2_datatask_keyno: params.button?.p2_datatask_keyno ?? this.ttP2DatataskKeyno },
                parameters: params.params
                    ? params.params
                    : {
                          ...(!!params.button ? params.button : { state: this.ttGoto, state_params: this.ttGotoParms }),
                          colgroup: this.node?.field,
                          rowkey: this.node?.key,
                          dataItem: this.node?.data,
                          row: this.grid!.gridApi?.getSelectedRows()[0],
                          rows: this.grid!.gridApi?.getSelectedRows(),
                          isSelected: this.grid!.options.gridfunc?.getIsSelectedRows(),
                          parameters: await this.gridService.getLoadDataParameters(this.grid?.ttOptions.dataTask?.loadData?.parameters),
                          datatask: this.grid!.ttOptions.dataTask?.loadData?.method,
                      },
                datataskEventEmitter: this.grid!.ttDatatask,
                navigateEventEmitter: this.grid!.ttNavigate,
                event: params.event,
                options: this.grid!.options,
                read: params.read ?? true,
            });
        } catch (error) {
            console.error(error);
            // this.modalService.openErrorDialog(`${error}`);
        }
    }

    private async setText() {
        if (!this.isValidButton()) return;

        const text = this.ttText;
        const translateText = this.ttTranslateText;

        if (translateText && text) {
            this.text = await this.translate.translate(text);
        } else {
            this.text = text ?? '';
        }
    }

    public getOverflowItemText(item: GridButton): string {
        return this.ttOverflowItemTextFallback?.(item) ?? item.text ?? '';
    }

    private async setTooltipText() {
        if (!this.isValidButton()) return;

        const tooltipText = this.ttTooltipText;
        const translateTooltipText = this.ttTranslateTooltipText;

        if (translateTooltipText && !!tooltipText) {
            this.tooltipText = await this.translate.translate(tooltipText);
        } else {
            this.tooltipText = tooltipText ?? '';
        }
    }

    private setIcon() {
        if (!this.isValidButton()) return;

        this.icon = this.gridService.getIconClasses(this.ttIcon);
    }

    private setSecondIcon() {
        if (!this.isValidButton()) return;

        this.secondIcon = this.gridService.getIconClasses(this.ttSecondIcon);
    }

    private setButtonClasses() {
        const kind = this.ttKind;

        if (!kind) {
            this.buttonClasses = 'ag-button ag-floating-filter-button-button tt-grid__toolbar-button';
            return;
        }

        let classes = 'tt-button';

        if (kind.includes('primary')) {
            classes += ' tt-button--primary';
        } else if (kind.includes('danger')) {
            classes += ' tt-button--danger';
        } else if (kind.includes('success')) {
            classes += ' tt-button--success';
        } else if (kind.includes('warning')) {
            classes += ' tt-button--warning';
        } else if (kind.includes('secondary')) {
            classes += ' tt-button--secondary';
        }

        classes += ' ' + kind;

        this.buttonClasses = classes;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttTranslateText']) {
            this.ttTranslateText = changes['ttTranslateText'].currentValue;
        }

        if (changes['ttTranslateTooltipText']) {
            this.ttTranslateTooltipText = changes['ttTranslateTooltipText'].currentValue;
        }

        if (changes['ttDisabled']) {
            this.ttDisabled = changes['ttDisabled'].currentValue;
        }

        if (changes['ttRowByRow']) {
            this.ttRowByRow = changes['ttRowByRow'].currentValue;
        }

        if (changes['ttReadOnClose']) {
            this.ttReadOnClose = changes['ttReadOnClose'].currentValue;
        }

        if (changes['ttRebindOnClose']) {
            this.ttRebindOnClose = changes['ttRebindOnClose'].currentValue;
        }

        if (changes['ttDisableIfNoSelected']) {
            this.ttDisableIfNoSelected = changes['ttDisableIfNoSelected'].currentValue;
        }

        if (changes['ttSaveSelectedRows']) {
            this.ttSaveSelectedRows = changes['ttSaveSelectedRows'].currentValue;
        }

        if (changes['ttForceNewTab']) {
            this.ttForceNewTab = changes['ttForceNewTab'].currentValue;
        }

        if (changes['ttText']) {
            this.setText();
        }

        if (changes['ttTooltipText']) {
            this.setTooltipText();
        }

        if (changes['ttIcon']) {
            this.setIcon();
        }

        if (changes['ttSecondIcon']) {
            this.setSecondIcon();
        }

        if (changes['ttKind'] || changes['ttContext']) {
            this.setButtonClasses();
        }
    }

    private isValidButton(): boolean {
        // return !!this.ttId && !!this.grid;
        return true;
    }

    private hasValidDatataskKeyno(): boolean {
        return true;
        // return !!this.ttP2DatataskKeyno && !isNaN(+this.ttP2DatataskKeyno) && +this.ttP2DatataskKeyno > 0;
    }

    private isStaticGotoType(type: GridButton['type'] | 'staticgoto' | 'statigoto' | undefined): boolean {
        return type === 'staticgoto' || type === 'statigoto';
    }

    private customCoerceBoolenProperty(value: any): boolean {
        if (['1', '0', 1, 0, 'true', true, 'false', false].includes(value)) {
            return ['1', 1, true, 'true'].includes(value as any);
        }
        return coerceBooleanProperty(value);
    }

    private getStateAndParams(button?: GridButton): { state: string; params?: string } {
        let state = button?.state ?? this.ttGoto ?? '';
        let params: GotoParms<string> | undefined = button?.state_params ?? this.ttGotoParms ?? '';

        try {
            if (typeof params === 'string' && !!params) {
                params = this.base64UrlDecode(params);
            }
        } catch (error) {
            console.error(error);
            params = undefined;
        }

        return { state, params: params as string | undefined };
    }
}
